import type { WallBlock, WallBlockType } from "./types";
import { newBlockId } from "./types";

const LEAF_TYPES = new Set<string>([
  "text", "header", "divider", "image", "quote", "news", "button", "globalCounter",
  "poll", "progress", "stat", "playerSpotlight", "featuredPreset", "featuredSpeedrun",
  "randomFact", "countdown", "link",
]);

export type WallRootBlock = WallBlock & { type: WallBlockType | "stack" };

export type StackInsert = "above" | "below";

export function isStackBlock(b: WallRootBlock): b is WallBlock & { type: "stack" } {
  return b.type === "stack";
}

export function stackItems(block: WallRootBlock): WallBlock[] {
  if (!isStackBlock(block)) return [];
  const raw = block.data.items;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (x): x is WallBlock =>
      !!x &&
      typeof x === "object" &&
      typeof (x as WallBlock).id === "string" &&
      typeof (x as WallBlock).type === "string" &&
      (x as WallBlock).type !== "stack" &&
      LEAF_TYPES.has((x as WallBlock).type),
  );
}

export function flattenWallBlocks(blocks: WallRootBlock[]): WallBlock[] {
  const out: WallBlock[] = [];
  for (const b of blocks) {
    if (isStackBlock(b)) out.push(...stackItems(b));
    else out.push(b);
  }
  return out;
}

export function createStackBlock(items: WallBlock[]): WallRootBlock {
  return { id: newBlockId(), type: "stack", data: { items: [...items] } };
}

/** Remove a top-level block by id; returns extracted block. */
export function extractTopLevel(blocks: WallRootBlock[], id: string): {
  blocks: WallRootBlock[];
  removed: WallRootBlock | null;
} {
  const idx = blocks.findIndex((b) => b.id === id);
  if (idx < 0) return { blocks, removed: null };
  const next = [...blocks];
  const [removed] = next.splice(idx, 1);
  return { blocks: next, removed: removed ?? null };
}

/** Merge dragged block into target (join as card stack). */
export function mergeWallBlocks(
  blocks: WallRootBlock[],
  dragId: string,
  targetId: string,
  insert: StackInsert,
): WallRootBlock[] {
  if (!dragId || dragId === targetId) return blocks;

  const { blocks: withoutDrag, removed: dragged } = extractTopLevel(blocks, dragId);
  if (!dragged) return blocks;

  const targetIdx = withoutDrag.findIndex((b) => b.id === targetId);
  if (targetIdx < 0) {
    return blocks;
  }

  const target = withoutDrag[targetIdx]!;
  let stackBlock: WallRootBlock;

  if (isStackBlock(dragged)) {
    const dragItems = stackItems(dragged);
    if (!dragItems.length) return withoutDrag;
    if (isStackBlock(target)) {
      const items = [...stackItems(target)];
      stackBlock = {
        ...target,
        data: {
          items: insert === "above" ? [...dragItems, ...items] : [...items, ...dragItems],
        },
      };
    } else {
      stackBlock = createStackBlock(
        insert === "above" ? [...dragItems, target] : [target, ...dragItems],
      );
    }
  } else if (isStackBlock(target)) {
    const items = [...stackItems(target)];
    stackBlock = {
      ...target,
      data: {
        items: insert === "above" ? [dragged, ...items] : [...items, dragged],
      },
    };
  } else {
    stackBlock = createStackBlock(
      insert === "above" ? [dragged, target] : [target, dragged],
    );
  }

  const next = [...withoutDrag];
  next[targetIdx] = stackBlock;
  return next;
}

/** Reorder top-level blocks (insert dragged before targetIndex gap). */
export function reorderWallBlocks(
  blocks: WallRootBlock[],
  dragId: string,
  insertIndex: number,
): WallRootBlock[] {
  const from = blocks.findIndex((b) => b.id === dragId);
  if (from < 0) return blocks;
  const next = [...blocks];
  const [item] = next.splice(from, 1);
  let idx = insertIndex;
  if (from < idx) idx -= 1;
  idx = Math.max(0, Math.min(next.length, idx));
  next.splice(idx, 0, item!);
  return next;
}

export function patchStackItem(
  blocks: WallRootBlock[],
  stackId: string,
  itemId: string,
  patch: Record<string, unknown>,
): WallRootBlock[] {
  return blocks.map((b) => {
    if (b.id !== stackId || !isStackBlock(b)) return b;
    const items = stackItems(b).map((item) =>
      item.id === itemId ? { ...item, data: { ...item.data, ...patch } } : item,
    );
    return { ...b, data: { items } };
  });
}

export function findStackItem(
  blocks: WallRootBlock[],
  itemId: string,
): { stack: WallRootBlock; item: WallBlock } | null {
  for (const b of blocks) {
    if (!isStackBlock(b)) continue;
    const item = stackItems(b).find((x) => x.id === itemId);
    if (item) return { stack: b, item };
  }
  return null;
}

/** Pull one block out of a stack as its own top-level block (placed after the stack). */
export function unstackItem(
  blocks: WallRootBlock[],
  stackId: string,
  itemId: string,
): WallRootBlock[] {
  const stackIdx = blocks.findIndex((b) => b.id === stackId);
  if (stackIdx < 0) return blocks;
  const stack = blocks[stackIdx]!;
  if (!isStackBlock(stack)) return blocks;

  const items = stackItems(stack);
  const item = items.find((x) => x.id === itemId);
  if (!item) return blocks;

  const remaining = items.filter((x) => x.id !== itemId);
  const next = [...blocks];

  if (remaining.length === 0) {
    next[stackIdx] = item;
    return next;
  }
  if (remaining.length === 1) {
    next.splice(stackIdx, 1, remaining[0]!, item);
    return next;
  }

  next[stackIdx] = { ...stack, data: { items: remaining } };
  next.splice(stackIdx + 1, 0, item);
  return next;
}

/** Split a card stack into separate top-level blocks at the same list position. */
export function splitStack(blocks: WallRootBlock[], stackId: string): WallRootBlock[] {
  const stackIdx = blocks.findIndex((b) => b.id === stackId);
  if (stackIdx < 0) return blocks;
  const stack = blocks[stackIdx]!;
  if (!isStackBlock(stack)) return blocks;

  const items = stackItems(stack);
  if (items.length <= 1) {
    if (items.length === 1) {
      const next = [...blocks];
      next[stackIdx] = items[0]!;
      return next;
    }
    return blocks.filter((_, i) => i !== stackIdx);
  }

  const next = [...blocks];
  next.splice(stackIdx, 1, ...items);
  return next;
}
